import type { MailDraftState } from '@shared/mail-draft';
export type { MailDraftState } from '@shared/mail-draft';

export interface MailComposeSession {
  state: MailDraftState;
  attachments: { file: File; name: string; size: number; preview?: string }[];
  deliveryRequestId: string;
  deliveryRequestConflict: string | null;
  draftId: string | null;
  missingAttachmentNames: string[];
  initialized: {
    prefilled: boolean;
    draft: string | null;
    reply: number | null;
    forward: number | null;
    activeForward: number | null;
    fromName: boolean;
    greeting: string;
  };
  previous?: MailComposeSession;
}

// Deliberately memory-only: File objects and delivery ownership must survive
// composer unmounts, but must never go into reusable backend drafts/localStorage.
const sessions = new Map<string, MailComposeSession>();
let scopedSessions = new WeakMap<object, Map<string, MailComposeSession>>();
function sessionStore(owner?: object) {
  if (!owner) return sessions;
  let store = scopedSessions.get(owner);
  if (!store) {
    store = new Map();
    scopedSessions.set(owner, store);
  }
  return store;
}
export function mailSessionKey(userId: string, projectId: string, contextKey: string): string {
  return JSON.stringify([userId, projectId, contextKey]);
}
export function readMailSession(key: string, owner?: object): MailComposeSession | undefined {
  return sessionStore(owner).get(key);
}
export function writeMailSession(key: string, session: MailComposeSession, owner?: object): void {
  sessionStore(owner).set(key, session);
}
export function deleteMailSession(key: string, owner?: object): void {
  sessionStore(owner).delete(key);
}
export function clearMailSessions(): void {
  sessions.clear();
  scopedSessions = new WeakMap();
}

export interface MailHistoryDraft {
  id: string;
  subject: string;
  updatedAt: string;
  state: MailDraftState;
}
export interface MailHistoryMessage {
  id: number | string;
  subject: string | null;
  emailDate: string | null;
  htmlContent: string | null;
  recipients: { email: string; type: 'to' | 'cc' }[];
  emailAccountId: string | null;
  hasAttachments: boolean;
}
export interface MailHistoryPage {
  drafts: MailHistoryDraft[];
  messages: MailHistoryMessage[];
  hasMore: boolean;
}
export function sortedMailHistory(pages: MailHistoryPage[], contextKey: string) {
  const drafts = new Map<string, MailHistoryDraft>();
  const messages = new Map<number | string, MailHistoryMessage>();
  for (const page of pages) {
    for (const draft of page.drafts || []) {
      if (contextKey === 'normal' || draft.state.contextKey === contextKey) drafts.set(draft.id, draft);
    }
    if (contextKey === 'normal') {
      for (const message of page.messages || []) messages.set(message.id, message);
    }
  }
  return [
    ...Array.from(drafts.values(), draft => ({ kind: 'draft' as const, date: draft.updatedAt, draft })),
    ...Array.from(messages.values(), message => ({ kind: 'sent' as const, date: message.emailDate, message })),
  ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export function reusableSentState(message: MailHistoryMessage): MailDraftState {
  return {
    subject: message.subject || '',
    htmlContent: message.htmlContent || '',
    recipients: message.recipients.filter(recipient => recipient.type === 'to' || recipient.type === 'cc'),
    selectedEmailAccount: message.emailAccountId || '',
    selectedSequences: [],
    replyTo: null,
    selectedForwardAttachmentIds: [],
    contextKey: 'normal',
    attachmentNames: [],
  };
}

export function reusableDraftState(state: MailDraftState, contextKey: string): MailDraftState {
  // Cross-context reuse is a new ordinary composition, never a switch of the
  // active authoritative source or an update to the original source draft.
  if (contextKey === 'normal' && state.contextKey !== 'normal') {
    return { ...state, contextKey: 'normal', replyTo: null, selectedForwardAttachmentIds: [] };
  }
  return state;
}
